import React, { useState, useEffect } from 'react';
import { Crown, Check, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { notesApi, getApiBaseUrl } from '../../services/cloudApi';

const CollabInviteMessage = ({ message, isOwnMessage, groupId, onAccepted }) => {
  const { t } = useTranslation('chat');
  const { authToken } = useAuth();
  const [loading, setLoading] = useState('');
  const [error, setError] = useState(null);
  // 'accepted' | 'rejected' | null
  const [fetchedStatus, setFetchedStatus] = useState(null);
  const [localStatus, setLocalStatus] = useState(null);

  const noteTitle = message?.note_title || message?.noteTitle || t('common:unnamed');
  const noteId    = message?.note_id   || message?.noteId;
  const messageId = message?.id;

  // Öncelik sırası: socket'ten gelen (message.collab_status) > local > backend fetch
  const status = message?.collab_status || localStatus || fetchedStatus;

  // Mount'ta backend'den güncel durumu çek
  useEffect(() => {
    if (!messageId || !authToken) return;
    let cancelled = false;
    (async () => {
      try {
        const base = await getApiBaseUrl();
        const res = await fetch(`${base}/notes/collab-invite/status?messageId=${messageId}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const data = await res.json();
        if (!cancelled && data?.status && data.status !== 'pending') {
          setFetchedStatus(data.status === 'accepted' ? 'accepted' : 'rejected');
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [messageId, authToken]);

  // Gerçek zamanlı güncelleme: ChatContext'in dispatch ettiği window event'i dinle
  useEffect(() => {
    if (!messageId) return;
    const handler = (e) => {
      if (String(e.detail.messageId) === String(messageId)) {
        setFetchedStatus(e.detail.status);
      }
    };
    window.addEventListener('notesplus-collab-invite-responded', handler);
    return () => window.removeEventListener('notesplus-collab-invite-responded', handler);
  }, [messageId]);

  const handleRespond = async (accept) => {
    if (!authToken || !messageId) return;
    setError(null);
    setLoading(accept ? 'accept' : 'reject');
    try {
      await notesApi.collabInviteRespond({
        token: authToken,
        messageId,
        groupId: groupId || null,
        accept,
      });
      setLocalStatus(accept ? 'accepted' : 'rejected');
      if (accept) {
        window.dispatchEvent(new CustomEvent('notesplus-reload-notes'));
        if (typeof onAccepted === 'function') onAccepted(noteId);
        window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
          detail: { type: 'success', title: t('collabInvite.acceptedToastTitle'), message: t('collabInvite.acceptedToastMessage') },
        }));
      }
    } catch (err) {
      const code = err?.payload?.error || err?.error;
      const msg  = err?.payload?.message || err?.message;
      setError(code === 'PLUS_REQUIRED' ? t('collabInvite.plusRequiredError') : (msg || t('collabInvite.genericError')));
    } finally {
      setLoading('');
    }
  };

  const cardBg  = isOwnMessage ? 'bg-[#1d3557] border-[#3d8ef0]/25' : 'bg-[var(--color-bg-muted)] border-white/[0.09]';
  const titleBg = isOwnMessage ? 'bg-white/[0.07]' : 'bg-[var(--color-bg-elevated)]';

  const StatusBadge = () => {
    if (status === 'accepted') {
      return (
        <div className="flex items-center gap-1.5 mt-2">
          <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <Check className="w-3 h-3 text-emerald-400" />
          </div>
          <span className="text-xs font-medium text-emerald-400">{t('collabInvite.accepted')}</span>
        </div>
      );
    }
    if (status === 'rejected') {
      return (
        <div className="flex items-center gap-1.5 mt-2">
          <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
            <X className="w-3 h-3 text-red-400" />
          </div>
          <span className="text-xs font-medium text-red-400">{t('collabInvite.rejected')}</span>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`rounded-2xl border ${cardBg} overflow-hidden min-w-[220px] max-w-[280px]`}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.06]">
        <Crown className="w-4 h-4 flex-shrink-0 text-amber-400" />
        <span className="text-[11px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wide">{t('collabInvite.header')}</span>
      </div>
      <div className="px-4 py-3">
        {isOwnMessage ? (
          /* Gönderenin görünümü */
          <>
            <p className="text-xs text-[var(--color-text-muted)] mb-2">
              {status ? (status === 'accepted' ? t('collabInvite.statusAccepted') : t('collabInvite.statusRejected')) : t('collabInvite.statusSent')}
            </p>
            <div className={`${titleBg} rounded-xl px-3 py-2.5`}>
              <p className="text-sm font-medium text-[var(--color-text-primary)] break-words line-clamp-2">"{noteTitle}"</p>
            </div>
            <StatusBadge />
          </>
        ) : status ? (
          /* Alıcı yanıtladıktan sonra */
          <>
            <div className={`${titleBg} rounded-xl px-3 py-2.5`}>
              <p className="text-sm font-medium text-[var(--color-text-primary)] break-words line-clamp-2">"{noteTitle}"</p>
            </div>
            <StatusBadge />
          </>
        ) : (
          /* Alıcının aktif davet kartı */
          <>
            <div className={`${titleBg} rounded-xl px-3 py-2.5 mb-3`}>
              <p className="text-sm font-medium text-[var(--color-text-primary)] break-words line-clamp-2">"{noteTitle}"</p>
              <p className="text-xs text-[var(--color-text-muted)] mt-1">{t('collabInvite.inviteDescription')}</p>
            </div>
            {error && <p className="text-xs text-red-400 mb-2">{error}</p>}
            <div className="flex gap-2">
              <button
                onClick={() => handleRespond(true)}
                disabled={!!loading}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-[#2563eb] hover:bg-[#3d8ef0] text-white disabled:opacity-50 transition-colors"
              >
                {loading === 'accept'
                  ? <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : <Check className="w-3.5 h-3.5" />}
                {t('collabInvite.accept')}
              </button>
              <button
                onClick={() => handleRespond(false)}
                disabled={!!loading}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold bg-[#161f31] hover:bg-[#24354f] text-[var(--color-text-secondary)] border border-white/[0.07] disabled:opacity-50 transition-colors"
              >
                {loading === 'reject'
                  ? <span className="w-3.5 h-3.5 border-2 border-[#7a96b0] border-t-transparent rounded-full animate-spin" />
                  : <X className="w-3.5 h-3.5" />}
                {t('collabInvite.reject')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CollabInviteMessage;
